import cx from "classnames"

import { useEffect, useState } from "react"
import { useCartContext } from "@/components/woocommerce/cartStore"
import { currency } from "@/lib/helpers/woocommerce"
import { useMeta } from "@/lib/contexts/appContext"
import useLocalShop from "@/lib/hooks/useLocalShop"
import { usePathData } from "@/lib/contexts/appContext"

import Button from "@/common/button/button"
import MiniCartItem from "@/components/woocommerce/MiniCartItem"
import FormattedMonetaryAmount from "@/components/woocommerce/FormattedMonetaryAmount"
import { trackViewCart } from "@/lib/helpers/tagManager"
import { itemInArrayHasOwn } from "@/lib/helpers/utils"

const MiniCart = ({ isOpen }) => {
  const { woocommerce } = useMeta()
  const { path } = usePathData()
  const {
    enabled,
    shop_url,
    checkout_url,
    i18n: {
      minicart: {
        header,
        clear_cart_button,
        empty_cart,
        empty_cart_cta,
        checkout_cta,
        shop_cta,
        total
      } = {}
    } = {}
  } = woocommerce
  const { data, clearCart } = useCartContext()
  const { deleteCart } = useLocalShop()
  const [showCTA, setShowCTA] = useState(false)

  useEffect(() => {
    const isShop = shop_url && shop_url.includes(path) && path !== "/"

    setShowCTA(!isShop)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, path])

  const classnames = cx(
    "fixed z-40 max-h-screen transition-all transform top-14 sm:top-20 xl:top-30 right-0 w-full sm:max-w-420",
    {
      "opacity-100 visible": isOpen,
      "opacity-0 invisible scale-95 -translate-y-2": !isOpen
    }
  )

  const { totals, items_count } = data

  const handleClearCart = () => {
    deleteCart()
    clearCart()
  }

  if (!enabled) {
    return null
  }

  if (isOpen) {
    trackViewCart(data)
  }

  if (!data.items?.length) {
    return (
      <div className={classnames}>
        <div className="p-5 text-left bg-white shadow-md py-7 sm:p-10 sm:pt-9 text-black-100">
          <header>
            <h4 className="mb-2 font-sans h5">{header}</h4>
            <p>{empty_cart}</p>
            {showCTA && (
              <footer className="grid grid-cols-1 gap-y-3">
                <Button
                  tagName="a"
                  ariaLabel={empty_cart_cta}
                  size="small"
                  className="mt-4 text-center"
                  theme="green"
                  href={shop_url ?? "#"}>
                  {empty_cart_cta}
                </Button>
              </footer>
            )}
          </header>
        </div>
      </div>
    )
  }

  const items = data.items.map((item) => (
    <MiniCartItem key={item.key} item={item} />
  ))

  const shipping = data.items.map((item) => {
    if (itemInArrayHasOwn(item.item_data, "is_shipping")) {
      return (
        <dl key={item.key} className="flex items-end justify-between">
          <dt>{item.name}</dt>
          <dd>{item.prices.price} kr</dd>
        </dl>
      )
    }
  })

  return (
    <div className={classnames}>
      <div className="relative flex flex-col p-5 text-left bg-white shadow-md max-h-screen-nav lg:max-h-screen-nav-lg py-7 sm:p-10 sm:pt-9 text-black-100">
        <header>
          <h4 className="flex items-center justify-between mb-4 font-sans h5">
            {header} ({items_count})
            <span
              className="text-sm cursor-pointer text-black-100/50"
              onClick={handleClearCart}>
              {clear_cart_button ?? "Töm varukorg"}
            </span>
          </h4>
        </header>
        <ul className="flex flex-col h-auto mb-4 overflow-y-auto border-b border-black-25 hide-checkmark">
          {items}
        </ul>
        <footer className="grid grid-cols-1 gap-y-3">
          {shipping}
          <dl className="flex items-end justify-between mb-2 font-bold">
            <dt>{total}</dt>
            <dd className="text-xl">
              <FormattedMonetaryAmount
                currency={currency}
                displayType="text"
                value={totals.total_price}
              />
            </dd>
          </dl>
          <Button
            tagName="a"
            size="small"
            ariaLabel={checkout_cta}
            className="text-center"
            theme="green"
            href={checkout_url || "#"}>
            {checkout_cta}
          </Button>
          <Button
            tagName="a"
            size="small"
            className="text-center"
            theme="light-lime"
            href={shop_url ?? "#"}>
            {shop_cta}
          </Button>
        </footer>
      </div>
    </div>
  )
}

export default MiniCart
